import axios from 'axios';
import { getApiUrl } from '../utils/apiConfig';

/**
 * Mark a session as completed (Professional only)
 * Triggers payment capture and updates session status
 * @param {string} sessionId - Session ID or coffeeChatId
 * @param {string} notes - Optional completion notes from professional
 * @returns {Promise} Response with captured payment details
 */
export const markSessionCompleted = async (sessionId, notes = '') => {
  try {
    const token = localStorage.getItem('token');

    if (!token) {
      throw new Error('Authentication required');
    }

    const response = await axios.post(
      getApiUrl(`/sessions/${sessionId}/complete`),
      { notes },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );

    return response.data;
  } catch (error) {
    console.error('Error marking session as completed:', error);

    // Re-throw with more specific error message
    if (error.response?.data?.error) {
      const apiError = new Error(error.response.data.error.message);
      apiError.code = error.response.data.error.code;
      apiError.status = error.response.status;
      throw apiError;
    }

    throw error;
  }
};

/**
 * Get session by ID
 * @param {string} sessionId - Session ID
 * @returns {Promise} Session data
 */
export const getSessionById = async (sessionId) => {
  try {
    const token = localStorage.getItem('token');

    if (!token) {
      throw new Error('Authentication required');
    }

    const response = await axios.get(
      getApiUrl(`/sessions/${sessionId}`),
      {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }
    );

    return response.data;
  } catch (error) {
    console.error('Error fetching session:', error);
    throw error;
  }
};

/**
 * Get user's sessions
 * @param {Object} params - Query parameters
 * @param {string} params.status - Filter by status (optional)
 * @param {number} params.page - Page number (optional)
 * @param {number} params.limit - Items per page (optional)
 * @returns {Promise} Sessions data with pagination
 */
export const getMySessions = async (params = {}) => {
  try {
    const token = localStorage.getItem('token');

    if (!token) {
      throw new Error('Authentication required');
    }

    const queryString = new URLSearchParams(params).toString();
    const url = getApiUrl(`/sessions/my${queryString ? `?${queryString}` : ''}`);

    const response = await axios.get(url, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    return response.data;
  } catch (error) {
    console.error('Error fetching sessions:', error);
    throw error;
  }
};

/**
 * Get upcoming sessions
 * @returns {Promise} Upcoming sessions
 */
export const getUpcomingSessions = async () => {
  try {
    const token = localStorage.getItem('token');

    if (!token) {
      throw new Error('Authentication required');
    }

    const response = await axios.get(
      getApiUrl('/sessions/upcoming'),
      {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }
    );

    return response.data;
  } catch (error) {
    console.error('Error fetching upcoming sessions:', error);
    throw error;
  }
};

/**
 * Cancel a session
 * @param {string} sessionId - Session ID
 * @returns {Promise} Cancellation confirmation
 */
export const cancelSession = async (sessionId) => {
  try {
    const token = localStorage.getItem('token');

    if (!token) {
      throw new Error('Authentication required');
    }

    const response = await axios.delete(
      getApiUrl(`/sessions/${sessionId}`),
      {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }
    );

    return response.data;
  } catch (error) {
    console.error('Error cancelling session:', error);
    throw error;
  }
};
